import React, { useEffect } from 'react';

import { Skeleton } from '@mui/material';
import dayjs from 'dayjs';

import ContentCard from '../../../../components/ContentCard';
import NotesDisplay from '../../../../components/NotesDisplay';
import {
  useDeleteLeadNoteMutation,
  useGetLeadNotesQuery,
  useNewLeadNoteMutation,
  useUpdateLeadNoteMutation,
} from '../../../../services/leads';
import { Lead } from '../../../../services/leads/types';
import { useGetStaffsQuery } from '../../../../services/staff';
import { TypeOption } from '../../../../services/types';
import { UserGroup } from '../../../../types/common';
import { joinNames } from '../../../../utils/common/formatText';
import useError from '../../../../utils/react/hooks/useError';

interface NotesCardProps {
  lead?: Lead,
}

const NotesCard = ({ lead } : NotesCardProps) => {
  const { data: leadNotes } = useGetLeadNotesQuery(
    {
      type: lead?.type || '',
      leadId: lead?.leadId || '',
    },
    { skip: !lead },
  );

  const [managerOpts, setManagerOpts] = React.useState<TypeOption[]>([]);
  const { data: managers } = useGetStaffsQuery({
    group: UserGroup.Managers,
    excludeDisabled: true,
  });
  useEffect(() => {
    setManagerOpts((managers?.data || []).map((manager) => ({
      value: manager.id,
      label: joinNames([manager.firstName, manager.lastName]),
    })) || []);
  }, [managers]);

  const [addNote, {
    isLoading: isAddNoteLoading,
    isSuccess: isAddNoteSuccess,
    isError: isAddNoteError,
    error: addNoteError,
  }] = useNewLeadNoteMutation();
  const [updateNote, {
    isError: isUpdateNoteError,
    error: updateNoteError,
  }] = useUpdateLeadNoteMutation();
  const [deleteNote, {
    isError: isDeleteNoteError,
    error: deleteNoteError,
  }] = useDeleteLeadNoteMutation();

  useError(isAddNoteError, addNoteError);
  useError(isUpdateNoteError, updateNoteError);
  useError(isDeleteNoteError, deleteNoteError);

  if (!lead || !managers) {
    return (
      <Skeleton variant="rectangular" height={256} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Notes">
      <NotesDisplay
        notes={leadNotes?.data || []}
        onNoteAdd={(note) => addNote({
          type: lead.type,
          leadId: lead.leadId,
          note,
        })}
        isNoteAddLoading={isAddNoteLoading}
        isNoteAddSuccess={isAddNoteSuccess}
        onNoteUpdate={(creationDatetime, newNote) => updateNote({
          type: lead.type,
          leadId: lead.leadId,
          creationDatetime,
          newAttributes: {
            note: newNote,
          },
        })}
        onNotePromote={(creationDatetime) => updateNote({
          type: lead.type,
          leadId: lead.leadId,
          creationDatetime,
          newAttributes: {
            priorityDatetime: dayjs().valueOf(),
          },
        })}
        onNoteArchive={(creationDatetime) => updateNote({
          type: lead.type,
          leadId: lead.leadId,
          creationDatetime,
          newAttributes: {
            active: false,
          },
        })}
        onNoteRestore={(creationDatetime) => updateNote({
          type: lead.type,
          leadId: lead.leadId,
          creationDatetime,
          newAttributes: {
            active: true,
          },
        })}
        onNoteDelete={(creationDatetime) => deleteNote({
          type: lead.type,
          leadId: lead.leadId,
          creationDatetime,
        })}
        onNoteDueDateChange={(creationDatetime, dueDatetime) => updateNote({
          type: lead.type,
          leadId: lead.leadId,
          creationDatetime,
          newAttributes: {
            dueDatetime,
          },
        })}
        onNoteAssigneeChange={(creationDatetime, assignee) => updateNote({
          type: lead.type,
          leadId: lead.leadId,
          creationDatetime,
          newAttributes: {
            assignee,
          },
        })}
        assigneeOptions={managerOpts}
      />
    </ContentCard>
  );
};

export default NotesCard;
